import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import { BugReport, ItemType } from 'shared/types/bug-report';
import { addFeatureToEpic } from 'shared/services/epicService';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { Sparkle, MagnifyingGlass } from '@phosphor-icons/react';
import * as Sentry from '@sentry/react';

interface AddFeatureToEpicModalProps {
  isOpen: boolean;
  epicId: number;
  epicTitle: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddFeatureToEpicModal: React.FC<AddFeatureToEpicModalProps> = ({
  isOpen,
  epicId,
  epicTitle,
  onClose,
  onSuccess
}) => {
  const { session } = useSession();
  const [features, setFeatures] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch all features (bug reports of type FEATURE) without an epic
  useEffect(() => {
    const fetchFeatures = async () => {
      setLoading(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/bug-reports`,
          { method: 'GET' },
          session
        );

        if (!response.ok) {
          throw new Error('Failed to fetch features');
        }

        const allBugReports: BugReport[] = await response.json();

        // Filter to only features without an epic
        const availableFeatures = allBugReports.filter(
          (report) => report.type === ItemType.FEATURE && !report.epicId
        );

        setFeatures(availableFeatures);
      } catch (error) {
        console.error('Error fetching features:', error);
        Sentry.captureException(error, {
          tags: { component: 'AddFeatureToEpicModal' },
          extra: { epicId }
        });
        toast.error('Fout bij laden features');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchFeatures();
    }
  }, [isOpen, session, epicId]);

  // Filtered features based on search
  const filteredFeatures = useMemo(() => {
    if (!searchQuery.trim()) return features;

    const query = searchQuery.toLowerCase();
    return features.filter(
      (f) =>
        f.title.toLowerCase().includes(query) ||
        f.description?.toLowerCase().includes(query) ||
        f.id.toString().includes(query)
    );
  }, [features, searchQuery]);

  const handleToggleFeature = (featureId: number) => {
    setSelectedFeatureIds((prev) =>
      prev.includes(featureId)
        ? prev.filter((id) => id !== featureId)
        : [...prev, featureId]
    );
  };

  const handleSelectAll = () => {
    if (selectedFeatureIds.length === filteredFeatures.length) {
      setSelectedFeatureIds([]);
    } else {
      setSelectedFeatureIds(filteredFeatures.map((f) => f.id));
    }
  };

  const handleSubmit = async () => {
    if (selectedFeatureIds.length === 0) {
      toast.error('Selecteer minimaal één feature');
      return;
    }

    setIsSubmitting(true);

    try {
      // Add each feature to the epic
      await Promise.all(
        selectedFeatureIds.map((featureId) =>
          addFeatureToEpic(epicId, featureId, session)
        )
      );

      toast.success(
        `${selectedFeatureIds.length} feature${selectedFeatureIds.length === 1 ? '' : 's'} toegevoegd aan epic`
      );
      handleClose();
      onSuccess();
    } catch (error: any) {
      console.error('Error adding features to epic:', error);
      Sentry.captureException(error, {
        tags: { component: 'AddFeatureToEpicModal' },
        extra: { epicId, featureIds: selectedFeatureIds }
      });
      toast.error(
        `Fout bij toevoegen features: ${error.message || 'Onbekende fout'}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedFeatureIds([]);
    setSearchQuery('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Features Toevoegen aan "{epicTitle}"
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <MagnifyingGlass
              size={20}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400"
            />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Zoek features op titel, beschrijving of ID..."
              className="pl-10"
            />
          </div>

          {/* Select All */}
          {filteredFeatures.length > 0 && (
            <div className="flex items-center gap-2 pb-2 border-b">
              <Checkbox
                checked={
                  selectedFeatureIds.length === filteredFeatures.length &&
                  filteredFeatures.length > 0
                }
                onCheckedChange={handleSelectAll}
              />
              <span className="text-sm text-neutral-600">
                Selecteer alle ({filteredFeatures.length})
              </span>
            </div>
          )}

          {/* Feature List */}
          {loading ? (
            <div className="text-center py-8 text-neutral-500">
              Features laden...
            </div>
          ) : filteredFeatures.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">
              {features.length === 0 ? (
                <div>
                  <p className="font-medium">Geen beschikbare features</p>
                  <p className="text-sm mt-1">
                    Alle features zijn al gekoppeld aan een epic
                  </p>
                </div>
              ) : (
                <div>
                  <p className="font-medium">Geen features gevonden</p>
                  <p className="text-sm mt-1">
                    Probeer een andere zoekopdracht
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredFeatures.map((feature) => (
                <div
                  key={feature.id}
                  className={`p-3 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                    selectedFeatureIds.includes(feature.id)
                      ? 'border-moss-primary bg-moss-primary/5'
                      : 'border-neutral-200'
                  }`}
                  onClick={() => handleToggleFeature(feature.id)}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={selectedFeatureIds.includes(feature.id)}
                      onCheckedChange={() => handleToggleFeature(feature.id)}
                      onClick={(e) => e.stopPropagation()}
                    />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <Sparkle
                          size={16}
                          weight="duotone"
                          className="text-blue-600 shrink-0 mt-0.5"
                        />
                        <div className="flex-1">
                          <h4 className="font-medium text-sm line-clamp-1">
                            {feature.title}
                          </h4>
                          <p className="text-xs text-neutral-600 line-clamp-2 mt-1">
                            {feature.description}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge
                              variant="outline"
                              className="text-xs bg-blue-50"
                            >
                              FEAT-{feature.id}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {feature.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-neutral-600">
              {selectedFeatureIds.length} feature
              {selectedFeatureIds.length === 1 ? '' : 's'} geselecteerd
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Annuleren
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || selectedFeatureIds.length === 0}
              >
                {isSubmitting
                  ? 'Bezig met toevoegen...'
                  : `${selectedFeatureIds.length} Feature${selectedFeatureIds.length === 1 ? '' : 's'} Toevoegen`}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
